/* eslint-disable padding-line-between-statements */
import React, {
    useState, useContext, useCallback, useEffect,useMemo
} from 'react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import _ from 'lodash';
import { useTranslator } from '@jutro/locale';
import PropTypes from 'prop-types';
import { ViewModelForm, ViewModelServiceContext, useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import { MLNameDetailsComponent, MLPhoneNumberComponent, MLEditContactDetailsComponent, MLPolicyNamedInsuredCandidatesComponent } from 'ml-components-common-react';
import { MLProductCodeUtil} from 'ml-portals-util-js';
import metadata from './SecondaryNamedInsuredComponent.metadata.json5';
import styles from './SecondaryNamedInsuredComponent.module.scss';
import messages from './SecondaryNamedInsuredComponent.messages';

const AgeAllowedToDrive = 16;
const today = new Date();
const PERSONAL_AUTO = 'PersonalAuto';
const PERSONAL_UMBRELLA = 'PersonalUmbrella';

function SecondaryNamedInsuredComponent(props) {
    const viewModelService = useContext(ViewModelServiceContext);
    const translator = useTranslator();
    const {
        submissionVM,
        updateWizardData,
        samePhone,
        setSamePhone,
        isAgent,
        doContactsHaveSamePhone,
        namedInsuredCandidates,
        id,
        onValidate,
        showValidationErrors,
        canEditCommFields,
        loadNamedInsuredCandidates,
        validateEmailAddress,
        isModernProduct,
        pniPriorAddress
    } = props;

    const { onValidate: setComponentValidation, isComponentValid } = useValidation(id);
    useEffect(() => {
        if (onValidate) {
            onValidate(isComponentValid, id);
        }
    }, [id, onValidate, isComponentValid]);

    const { refreshData } = useDataRefresh();

    const productCode = _.get(submissionVM, 'baseData.productCode.value');
    const personalAutoDriversPath = 'lobData.personalAuto.coverables.drivers.value';
    const personalUmbrellaVehicleOperatorsPath = 'lobData.personalUmbrella.coverables.vehicleOperators.value';

    const isQuoteAndBuy = _.get(submissionVM, 'baseData.portalCode_ML.value') === 'QB';

    const secondaryNamedInsured = _.get(submissionVM, 'baseData.secondaryNamedInsured_ML.value');
    const secondaryHomeNumVal = _.get(submissionVM, 'baseData.secondaryNamedInsured_ML.homeNumber.value');
    const primaryNamedInsuredValue = _.get(submissionVM, 'baseData.primaryNamedInsured_ML.value');
    const accountHolderHomeNumberVal = _.get(submissionVM, 'baseData.accountHolder.homeNumber.value');
    const isDwellingDeededToBusinessOrTrust = _.get(submissionVM, 'baseData.deedInfo_Ext.value');
    const isHomeowners = MLProductCodeUtil.isHomeOwners(submissionVM.baseData.productCode.value);
    const isLLHOrLLC = MLProductCodeUtil.isHomeOwnersPartialTypeLandlordHome(submissionVM.lobData?.homeowners?.coverables?.coveragePartType_ML?.value?.code)
        || MLProductCodeUtil.isHomeOwnersPartialTypeLandlordCondo(submissionVM.lobData?.homeowners?.coverables?.coveragePartType_ML?.value?.code);
    const isHomeownersAndLLHOrLLC = isHomeowners && isLLHOrLLC;
    const isModernProductHomeowners = isModernProduct && isHomeowners
    //Changes dor Spouse Prior address
    const priorMailingAddressValue = _.get(submissionVM, 'baseData.sniPriorAddressQuestion_Ext.value');
    const policyAddress = _.get(submissionVM, 'baseData.policyAddress');
    const [priorAddress, setPriorAddress] = useState(_.get(submissionVM, 'baseData.sniPriorAddress_Ext'));
    const priorAddressSameAsPniValue = _.get(submissionVM, 'baseData.sniSameAsPniPriorAddress_Ext.value', true);
    const pniPriorMailingAddressValue = _.get(submissionVM, 'baseData.priorAddressQuestion_Ext.value',false);
    const isSniPfmReportStatusNoHit = _.get(submissionVM, 'baseData.isSniPfmReportStatusNoHit.value',false);
    
    const [
        secondaryNamedInsuredFlag,
        setSecondaryNamedInsuredFlag
    ] = useState(!_.isUndefined(secondaryNamedInsured));
    const [originalSNIContactDetails, setOriginalSNIContactDetails] = useState({});
    const [occupationtypelist, updateOccupationtypelist] = useState();

    const changeExistingDriverFields = useCallback((currSubmissionVM, sniContact, path) => {
        if (productCode === PERSONAL_AUTO) {
            const drivers = _.get(currSubmissionVM, personalAutoDriversPath, []);
            if (drivers.length > 0) {
                const existingDriver = drivers.find((driver) => driver.person.publicID === sniContact.publicID);
                if (existingDriver) {
                    if (!path) {
                        existingDriver.dateOfBirth = sniContact.dateOfBirth;
                        existingDriver.emailAddress_ML = sniContact.emailAddress1;
                    } else if (path.includes('dateOfBirth')) {
                        existingDriver.dateOfBirth = sniContact.dateOfBirth;
                    } else if (path.includes('emailAddress1')) {
                        existingDriver.emailAddress_ML = sniContact.emailAddress1;
                    }
                }
            }
        } else if (productCode === PERSONAL_UMBRELLA) {
            const drivers = _.get(currSubmissionVM, personalUmbrellaVehicleOperatorsPath, []);
            if (drivers.length > 0) {
                const existingDriver = drivers.find((driver) => driver.contact.publicID === sniContact.publicID);
                if (existingDriver) {
                    if (!path) {
                        existingDriver.dateOfBirth = sniContact.dateOfBirth;
                        existingDriver.emailAddress_ML = sniContact.emailAddress1;
                    } else if (path.includes('dateOfBirth')) {
                        existingDriver.dateOfBirth = sniContact.dateOfBirth;
                    } else if (path.includes('emailAddress1')) {
                        existingDriver.emailAddress = sniContact.emailAddress1;
                    }
                }
            }
        }
    }, [productCode]);

    const writeValue = useCallback(
        (value, path) => {
            _.set(submissionVM, path, value);
            if (path) {
                const sniContact = _.get(submissionVM, 'baseData.secondaryNamedInsured_ML.value');
                if (sniContact && (path.includes('dateOfBirth') || path.includes('emailAddress1'))) {
                    changeExistingDriverFields(submissionVM, sniContact, path);
                }
            }
            updateWizardData(submissionVM);
        },
        [changeExistingDriverFields, submissionVM, updateWizardData]
    );

    const updatePhoneProperties = useCallback(() => {
        const accountFromPath = isAgent ? 'baseData.primaryNamedInsured_ML' : 'baseData.accountHolder';

        const primaryPhoneType = _.get(submissionVM, `${accountFromPath}.primaryPhoneType.value`);
        const workNumber = _.get(submissionVM, `${accountFromPath}.workNumber.value`);
        const cellNumber = _.get(submissionVM, `${accountFromPath}.cellNumber.value`);
        const homeNumber = _.get(submissionVM, `${accountFromPath}.homeNumber.value`);
        const homeTcpaConsent = _.get(submissionVM, `${accountFromPath}.tcpaConsentHomeNumber_ML.value`);
        const cellTcpaConsent = _.get(submissionVM, `${accountFromPath}.tcpaConsentCellNumber_ML.value`);
        const workTcpaConsent = _.get(submissionVM, `${accountFromPath}.tcpaConsentWorkNumber_ML.value`);
        const homeTcpaTextConsent = _.get(submissionVM, `${accountFromPath}.tcpaConsentHomeNumberText_ML.value`);
        const cellTcpaTextConsent = _.get(submissionVM, `${accountFromPath}.tcpaConsentCellNumberText_ML.value`);
        const workTcpaTextConsent = _.get(submissionVM, `${accountFromPath}.tcpaConsentWorkNumberText_ML.value`);

        _.set(submissionVM, 'baseData.secondaryNamedInsured_ML.primaryPhoneType.value', primaryPhoneType);
        _.set(submissionVM, 'baseData.secondaryNamedInsured_ML.workNumber.value', workNumber);
        _.set(submissionVM, 'baseData.secondaryNamedInsured_ML.homeNumber.value', homeNumber);
        _.set(submissionVM, 'baseData.secondaryNamedInsured_ML.cellNumber.value', cellNumber);
        _.set(submissionVM, 'baseData.secondaryNamedInsured_ML.tcpaConsentHomeNumber_ML.value', homeTcpaConsent);
        _.set(submissionVM, 'baseData.secondaryNamedInsured_ML.tcpaConsentHomeNumberText_ML.value', homeTcpaTextConsent);
        _.set(submissionVM, 'baseData.secondaryNamedInsured_ML.tcpaConsentCellNumber_ML.value', cellTcpaConsent);
        _.set(submissionVM, 'baseData.secondaryNamedInsured_ML.tcpaConsentCellNumberText_ML.value', cellTcpaTextConsent);
        _.set(submissionVM, 'baseData.secondaryNamedInsured_ML.tcpaConsentWorkNumber_ML.value', workTcpaConsent);
        _.set(submissionVM, 'baseData.secondaryNamedInsured_ML.tcpaConsentWorkNumberText_ML.value', workTcpaTextConsent);
    }, [isAgent, submissionVM]);

    const clearPhoneProperties = useCallback(() => {
        const secondaryNamedInsured_ML = _.get(submissionVM, 'baseData.secondaryNamedInsured_ML');
        const secondaryNamedInsuredCandidate = namedInsuredCandidates.find((insuredCandidate) => insuredCandidate.publicID === secondaryNamedInsured_ML.publicID.value)
        if (secondaryNamedInsuredCandidate) {
            _.set(submissionVM, 'baseData.secondaryNamedInsured_ML.primaryPhoneType.value', secondaryNamedInsuredCandidate.primaryPhoneType);
            _.set(submissionVM, 'baseData.secondaryNamedInsured_ML.workNumber.value', secondaryNamedInsuredCandidate.workNumber);
            _.set(submissionVM, 'baseData.secondaryNamedInsured_ML.homeNumber.value', secondaryNamedInsuredCandidate.homeNumber);
            _.set(submissionVM, 'baseData.secondaryNamedInsured_ML.cellNumber.value', secondaryNamedInsuredCandidate.cellNumber);
            _.set(submissionVM, 'baseData.secondaryNamedInsured_ML.tcpaConsentHomeNumber_ML.value', secondaryNamedInsuredCandidate.tcpaConsentHomeNumber_ML);
            _.set(submissionVM, 'baseData.secondaryNamedInsured_ML.tcpaConsentHomeNumberText_ML.value', secondaryNamedInsuredCandidate.tcpaConsentHomeNumberText_ML);
            _.set(submissionVM, 'baseData.secondaryNamedInsured_ML.tcpaConsentWorkNumber_ML.value', secondaryNamedInsuredCandidate.tcpaConsentWorkNumber_ML);
            _.set(submissionVM, 'baseData.secondaryNamedInsured_ML.tcpaConsentCellNumberText_ML.value', secondaryNamedInsuredCandidate.tcpaConsentCellNumberText_ML);
            _.set(submissionVM, 'baseData.secondaryNamedInsured_ML.tcpaConsentWorkNumber_ML.value', secondaryNamedInsuredCandidate.tcpaConsentWorkNumber_ML);
            _.set(submissionVM, 'baseData.secondaryNamedInsured_ML.tcpaConsentWorkNumberText_ML.value', secondaryNamedInsuredCandidate.tcpaConsentWorkNumberText_ML);
        } else {
            _.set(submissionVM, 'baseData.secondaryNamedInsured_ML.primaryPhoneType.value', undefined);
            _.set(submissionVM, 'baseData.secondaryNamedInsured_ML.workNumber.value', undefined);
            _.set(submissionVM, 'baseData.secondaryNamedInsured_ML.homeNumber.value', undefined);
            _.set(submissionVM, 'baseData.secondaryNamedInsured_ML.cellNumber.value', undefined);
            _.set(submissionVM, 'baseData.secondaryNamedInsured_ML.tcpaConsentHomeNumber_ML.value', undefined);
            _.set(submissionVM, 'baseData.secondaryNamedInsured_ML.tcpaConsentHomeNumberText_ML.value', undefined);
            _.set(submissionVM, 'baseData.secondaryNamedInsured_ML.tcpaConsentWorkNumber_ML.value', undefined);
            _.set(submissionVM, 'baseData.secondaryNamedInsured_ML.tcpaConsentCellNumberText_ML.value', undefined);
            _.set(submissionVM, 'baseData.secondaryNamedInsured_ML.tcpaConsentWorkNumber_ML.value', undefined);
            _.set(submissionVM, 'baseData.secondaryNamedInsured_ML.tcpaConsentWorkNumberText_ML.value', undefined);
        }
    }, [namedInsuredCandidates, submissionVM]);

    const setInitialContactDetails = () => {
        setOriginalSNIContactDetails({
            originalEmailAddress: _.get(submissionVM, 'baseData.secondaryNamedInsured_ML.emailAddress1.value'),
            originalHomeNumber: _.get(submissionVM, 'baseData.secondaryNamedInsured_ML.homeNumber.value'),
            originalCellNumber: _.get(submissionVM, 'baseData.secondaryNamedInsured_ML.cellNumber.value'),
            originalWorkNumber: _.get(submissionVM, 'baseData.secondaryNamedInsured_ML.workNumber.value')
        });
    };

    const disableContactField = (value) => {
        return (isAgent && !canEditCommFields && value);
    };

    useEffect(() => {
        if (secondaryNamedInsured && samePhone) {
            updatePhoneProperties();
        }
    }, [clearPhoneProperties, samePhone, secondaryNamedInsured, updatePhoneProperties]);

    useEffect(() => {
        const initOccupationTypeList = () => {
            let categories;
            if (!isModernProductHomeowners) return;
            categories = submissionVM?.baseData?.primaryNamedInsured_ML?.occupationType_Ext?.aspects?.availableValues.filter((value) => {
                return value.code.endsWith('_np') || value.code === 'military' || value.code === 'other';
            });
            if(!categories) return;
            const categoryCodes = categories.map((category) => ({
                code: category.code,
                name: translator({
                    id: category.name,
                    defaultMessage: category.name
                })
            }));

            updateOccupationtypelist(categoryCodes);
        }

        initOccupationTypeList();

    }, [isModernProductHomeowners]);

    useEffect(() => {
        setInitialContactDetails();
    }, [])

    const setSecondaryNamedInsured = useCallback((insuredCandidate, flag) => {
        let dataHasChanged = false;

        if (!flag && !secondaryNamedInsuredFlag) {
            if (_.get(submissionVM,'baseData.secondaryNamedInsured_ML.value') !== undefined) {
                _.set(submissionVM, 'baseData.secondaryNamedInsured_ML', undefined);
                dataHasChanged = true;
            }
        } else if (!insuredCandidate) {
            const secondaryNamedInsuredObj = {
                accountHolder: false,
                subtype: 'Person',
                primaryAddress: submissionVM.baseData.policyAddress.value
            };
            const newSecondaryNamedInsured = viewModelService.create(secondaryNamedInsuredObj, 'pc', 'edge.capabilities.policycommon.accountcontact.dto.AccountContactDTO');

            if (!_.isEqual(_.get(submissionVM, 'baseData.secondaryNamedInsured_ML.value'), newSecondaryNamedInsured.value)) {
                _.set(submissionVM, 'baseData.secondaryNamedInsured_ML.value', newSecondaryNamedInsured.value);
                dataHasChanged = true;
            }

            setSamePhone(true);
        } else {
            let sniContact;
            if (submissionVM.quoteID.value) {
                sniContact = submissionVM.value.findPerson(submissionVM.value, insuredCandidate);
                sniContact.accountHolder = false;
                sniContact.subtype = 'Person';
                if (!sniContact.primaryAddress) {
                    sniContact.primaryAddress = submissionVM.baseData.policyAddress.value;
                }
            } else {
                sniContact = insuredCandidate;
            }
            if (!_.isEqual(_.get(submissionVM, 'baseData.secondaryNamedInsured_ML.value'), sniContact)) {
                _.set(submissionVM, 'baseData.secondaryNamedInsured_ML.value', sniContact);
                dataHasChanged = true;
            }

            changeExistingDriverFields(submissionVM, sniContact);

            setSamePhone(true);
            clearPhoneProperties();
        }

        if (dataHasChanged) {
            updateWizardData(submissionVM);
        }

        setInitialContactDetails()
    }, [changeExistingDriverFields, clearPhoneProperties, secondaryNamedInsuredFlag, setSamePhone, submissionVM, updateWizardData, viewModelService]);

    const accountExists = useCallback(() => {
        return !!_.get(submissionVM, 'baseData.accountNumber.value');
    }, [submissionVM]);

    useEffect(() => {
        if (!secondaryNamedInsuredFlag) {
            setSecondaryNamedInsured(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [secondaryNamedInsuredFlag]);

    const secondaryNamedInsuredAction = useCallback(() => {
        const flag = !secondaryNamedInsuredFlag;
        setSecondaryNamedInsuredFlag(flag);
        if(!flag || (flag && !accountExists())) {
            setSecondaryNamedInsured(undefined, true);
        }
    }, [accountExists, secondaryNamedInsuredFlag, setSecondaryNamedInsured]);

    const setSecondaryPhoneFromSamePhone = useCallback(() => {
        const samePhoneFlag = !samePhone;
        setSamePhone(!samePhone);

        if (secondaryNamedInsured && samePhoneFlag) {
            updatePhoneProperties();
        } else {
            clearPhoneProperties(secondaryNamedInsured.value);
        }
        setInitialContactDetails();
    }, [clearPhoneProperties, samePhone, secondaryNamedInsured, setSamePhone, updatePhoneProperties]);

    const updateContactVM = useCallback((model) => {
        let subPerson;
        if (submissionVM.quoteID.value) {
            subPerson = submissionVM.value.findPerson(submissionVM.value, model.value);
        } else {
            subPerson = model.value;
        }
        _.set(submissionVM, 'baseData.secondaryNamedInsured_ML.value', subPerson);
        updateWizardData(submissionVM);
    }, [submissionVM, updateWizardData]);

    const onBlurEmailAddress = useCallback(() => {
        validateEmailAddress(_.get(submissionVM, 'baseData.secondaryNamedInsured_ML'));
    }, [validateEmailAddress]);

    const emailTrim = ( event ) => {
        let emailAddress1 =  event.target.value;
        if(emailAddress1 !== "" && emailAddress1 !== undefined){
            emailAddress1 = emailAddress1.trim();
            event.target.value = emailAddress1;
        }
    }

    const getSecondaryNamedInsuredContent = useCallback(() => {
        if (isHomeownersAndLLHOrLLC && isDwellingDeededToBusinessOrTrust === true) {
            return translator(messages.secondPersonInControl);
        }
        return translator(messages.secondaryNamedInsured);
    }, [
        isHomeownersAndLLHOrLLC,
        isDwellingDeededToBusinessOrTrust,
    ]);

    const sni = submissionVM?.baseData?.secondaryNamedInsured_ML?.value;

    const isEmpty = (value) =>
        value === undefined || value === null || value === '';  

    const maritalStatusError =
        isModernProductHomeowners && isEmpty(sni?.maritalStatus);

    const educationError =
        isModernProductHomeowners && isEmpty(sni?.educationLevel_ML);

    const occupationError =
        isModernProductHomeowners && isEmpty(sni?.occupationType_Ext);

    const relationshipError =
        isModernProductHomeowners && isEmpty(sni?.insuredRelationType_ML);

    const syncAddresses = (fromAddress, toAddress) => {
        _.set(toAddress, 'addressLine1', fromAddress.addressLine1);
        _.set(toAddress, 'addressLine2', fromAddress.addressLine2);
        _.set(toAddress, 'city', fromAddress.city);
        _.set(toAddress, 'state', fromAddress.state);
        _.set(toAddress, 'postalCode', fromAddress.postalCode);
        _.set(toAddress, 'country', fromAddress.country);
        _.set(toAddress, 'displayName', fromAddress.displayName);
    };

    const updatePriorAddress = useCallback(() => {
        const flag = !priorMailingAddressValue;
        const clonedSubmissionVM = _.clone(submissionVM);
        _.set(clonedSubmissionVM, 'baseData.sniPriorAddressQuestion_Ext.value', flag);
        if (pniPriorMailingAddressValue && !_.isUndefined(pniPriorAddress)) {
            syncAddresses(pniPriorAddress, clonedSubmissionVM.baseData.sniPriorAddress_Ext);
        } else if(!flag) {
            syncAddresses(policyAddress, clonedSubmissionVM.baseData.sniPriorAddress_Ext);
        }
        else {
            clonedSubmissionVM.baseData.sniPriorAddress_Ext = {};
        }
        setPriorAddress(clonedSubmissionVM.baseData.sniPriorAddress_Ext);
        updateWizardData(clonedSubmissionVM);
    }, [pniPriorAddress, priorMailingAddressValue, pniPriorMailingAddressValue]);

    const updateSniAddress = useCallback((flag) => {
        const clonedSubmissionVM = _.clone(submissionVM);
        _.set(clonedSubmissionVM, 'baseData.sniSameAsPniPriorAddress_Ext.value', flag);
        if (flag && !_.isUndefined(pniPriorAddress)) {
            syncAddresses(pniPriorAddress, clonedSubmissionVM.baseData.sniPriorAddress_Ext);
        } else {
            clonedSubmissionVM.baseData.sniPriorAddress_Ext = {};
        }
        updateWizardData(clonedSubmissionVM);
        refreshData();
    }, [pniPriorAddress, priorAddressSameAsPniValue, priorMailingAddressValue]);

    const isSniContainerVisible = () => {
        return isModernProductHomeowners && !isLLHOrLLC && sni?.insuredRelationType_ML === "spouse" && isSniPfmReportStatusNoHit
    }

    const overrideProps = {
        relationship: {
            visible: isModernProductHomeowners && !isLLHOrLLC,
            validationMessages: relationshipError ? [translator(messages.fieldRequired)] : []
        },
        sniMaritalStatusML: {
            visible: !isLLHOrLLC,
            validationMessages: maritalStatusError ? [translator(messages.fieldRequired)] : []
        },
        educationLevel: {
            visible: isModernProductHomeowners,
            validationMessages: educationError ? [translator(messages.fieldRequired)] : []
        },
        occupation: {
            visible: isModernProductHomeowners,
            availableValues: occupationtypelist,
            validationMessages: occupationError ? [translator(messages.fieldRequired)] : []
        },
        updateInfoAboveText: { content: isModernProduct && isHomeownersAndLLHOrLLC && isDwellingDeededToBusinessOrTrust === true ? translator(messages.secPersonInControl) : translator(messages.secNamedInsured) },
        isSecondaryNamedInsuredNeeded: {
            label: getSecondaryNamedInsuredContent(),
            value: secondaryNamedInsuredFlag,
            onValueChange: secondaryNamedInsuredAction,
            tooltip: !isAgent && {
                title: translator(messages.addSecondaryNamedInsuredTooltip),
                text: translator(messages.addSecondaryNamedInsuredTooltipText)
            },
            // eslint-disable-next-line no-nested-ternary
            className: isQuoteAndBuy ? "radioButtonQnB" : isAgent ? 'vertical-spacing-20' : ''
        },
        policyNamedInsuredCandidatesComponentContainer: {
            visible: secondaryNamedInsuredFlag === true,
            className: isQuoteAndBuy ? 'overhidden-pop-up' : ''
        },
        updateInfoAboveContainer: {
            visible: accountExists()
        },
        policyNamedInsuredCandidatesComponent: {
            visible: accountExists(),
            submissionVM,
            initPublicID: secondaryNamedInsured && secondaryNamedInsured.publicID,
            setNamedInsured: setSecondaryNamedInsured,
            namedInsuredCandidates,
            loadNamedInsuredCandidates,
            isSecondaryNamedInsured: true
        },
        secondaryNamedInsuredContainer: {
            visible: secondaryNamedInsured !== undefined,
            className: !isAgent ? 'ml-popover pointer-left clearfix' : ''
        },
        phoneCheckboxField: {
            onValueChange: setSecondaryPhoneFromSamePhone,
            value: samePhone
        },
        secondaryPhoneNumber: {
            visible: isQuoteAndBuy ? !isQuoteAndBuy : samePhone !== true && !isAgent,
            updateContactVM,
            contactVM: _.get(submissionVM, 'baseData.secondaryNamedInsured_ML')
        },
        secondaryNameDetailsComponent: {
            personVM: _.get(submissionVM, 'baseData.secondaryNamedInsured_ML'),
            onValidate,
            showValidationErrors,
            onModelChange: writeValue,
            submissionVM
        },
        sniPriorAddressContainer: {
            visible: isSniContainerVisible()
        },
        sniPriorAddressDetailsContainer: {
            visible: priorMailingAddressValue
        },
        sniPriorAddressRadioButton: {
            onValueChange: updatePriorAddress,
            value: priorMailingAddressValue,
        },
        sniPriorAddressSameAsPni: {
            visible : priorMailingAddressValue && pniPriorMailingAddressValue,
            value : priorAddressSameAsPniValue,
            onValueChange : updateSniAddress
        },
        sniPriorAddresslookup: {
            id: 'sniPriorAddresslookup',
            value: priorAddress,
            showValidationErrors,
            onValueChange: writeValue,
            initialLockStateCity: false,
        },
        dateOfBirth: {
            minDate:  {
                year: 1900,
                month: today.getMonth(),
                day: today.getDate()
            },
            maxDate:  {
                year: 2100,
                month: today.getMonth(),
                day: today.getDate()
            }
        },
        emailAddress: {
            onValueChange: writeValue,
            disabled: disableContactField(originalSNIContactDetails.originalEmailAddress),
            onBlur: onBlurEmailAddress,
            onFocus: (event) => emailTrim(event)
        },

        secondaryEditContactDetails: {
            visible: samePhone !== true && isAgent,
            accountContactDetailsVm: _.get(submissionVM, 'baseData.secondaryNamedInsured_ML'),
            isAgent: true,
            showPrimaryEmailAddress: false,
            writeValue,
            path: 'baseData.secondaryNamedInsured_ML',
            showValidationErrors,
            updateContactVM,
            canEditCommFields,
            originalContactDetails: originalSNIContactDetails
        }
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveComponentMap: {
            mlPolicyNamedInsuredCandidatesComponent: MLPolicyNamedInsuredCandidatesComponent,
            mlNameDetailsComponent: MLNameDetailsComponent,
            phoneNumberComponent: MLPhoneNumberComponent,
            mlEditContactDetailsComponent: MLEditContactDetailsComponent
        }
    };

    return (
        <ViewModelForm
            uiProps={metadata.pageContent}
            model={submissionVM}
            overrideProps={overrideProps}
            onValueChange={writeValue}
            classNameMap={resolvers.resolveClassNameMap}
            componentMap={resolvers.resolveComponentMap}
            onValidationChange={setComponentValidation}
            showErrors={showValidationErrors}
        />
    );
}

SecondaryNamedInsuredComponent.propTypes = {
    submissionVM: PropTypes.shape({
        baseData: PropTypes.object
    }).isRequired,
    updateWizardData: PropTypes.func.isRequired,
    samePhone: PropTypes.bool.isRequired,
    setSamePhone: PropTypes.func.isRequired,
    isAgent: PropTypes.bool,
    doContactsHaveSamePhone: PropTypes.func,
    loadNamedInsuredCandidates: PropTypes.func
};

SecondaryNamedInsuredComponent.defaultProps = {
    isAgent: false,
    doContactsHaveSamePhone: _.noop
};

export default SecondaryNamedInsuredComponent;
